import axios from 'axios';

import { authRefresh } from '@modules/auth/core/actions';
import { activeLocale } from '@shared/i18n/Metronici18n';

const TOKEN_REFRESH_TIMEOUT_MS = 10000;

const setupAxios = (store: any) => {
  axios.defaults.baseURL = process.env.VITE_API_URI;
  axios.defaults.withCredentials = true;

  // The locale every cd-api response is rendered in. Sent once here rather than
  // per call site, so any endpoint can localise what it returns without a change
  // on this side — today the configurator reference data and the pet's breeds,
  // allergies and health issues, which live in the petcon database rather than in
  // the message catalogues. cd-api falls back to German for an unknown value.
  axios.defaults.headers.common['Accept-Language'] = activeLocale();

  // Refresh lock — prevents multiple parallel 401s from each triggering their own refresh
  let isRefreshing = false;
  let refreshSubscribers: Array<(token: string) => void> = [];
  let refreshFailSubscribers: Array<(error: Error) => void> = [];

  const subscribeToRefresh = (
    onSuccess: (token: string) => void,
    onFailure: (error: Error) => void,
  ) => {
    refreshSubscribers.push(onSuccess);
    refreshFailSubscribers.push(onFailure);
  };

  const onRefreshSuccess = (newToken: string) => {
    refreshSubscribers.forEach((cb) => cb(newToken));
    refreshSubscribers = [];
    refreshFailSubscribers = [];
  };

  const onRefreshFailure = (error: Error) => {
    refreshFailSubscribers.forEach((cb) => cb(error));
    refreshSubscribers = [];
    refreshFailSubscribers = [];
  };

  const isAuthEndpoint = (url?: string) => url?.startsWith('/api/v1/auth');

  axios.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const accessToken = state.auth.data?.accessToken;
      if (accessToken && !isAuthEndpoint(config.url)) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !isAuthEndpoint(originalRequest.url)
      ) {
        originalRequest._retry = true;

        // If another request is already refreshing, queue this one
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeToRefresh(
              (newToken: string) => {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                resolve(axios(originalRequest));
              },
              (refreshError: Error) => {
                reject(refreshError);
              },
            );
          });
        }

        isRefreshing = true;
        const currentState = store.getState();
        const currentAccessToken = currentState.auth.data?.accessToken;

        if (!currentAccessToken) {
          console.warn('[Auth] No accessToken in store — cannot refresh');
          isRefreshing = false;
          return Promise.reject(error);
        }

        store.dispatch(authRefresh({ accessToken: currentAccessToken }));

        const waitForTokenRefresh = new Promise<string | null>((resolve, reject) => {
          let elapsedMs = 0;
          const pollIntervalMs = 100;

          const checkState = setInterval(() => {
            elapsedMs += pollIntervalMs;

            const newState = store.getState();
            const newAccessToken = newState.auth.data?.accessToken;

            if (newAccessToken && newAccessToken !== currentAccessToken) {
              clearInterval(checkState);
              resolve(newAccessToken);
            } else if (!newState.auth.data) {
              // Store was wiped (forced logout) — no point waiting
              console.warn('[Auth] Auth state cleared (session expired) — stopping wait');
              clearInterval(checkState);
              reject(new Error('Session expired'));
            } else if (elapsedMs >= TOKEN_REFRESH_TIMEOUT_MS) {
              console.warn(`[Auth] Token refresh timed out after ${TOKEN_REFRESH_TIMEOUT_MS}ms`);
              clearInterval(checkState);
              reject(new Error('Token refresh timed out'));
            } else if (!newState.auth.pending && newState.auth.error) {
              console.warn('[Auth] Refresh saga finished with error — stopping wait');
              clearInterval(checkState);
              reject(new Error('Token refresh failed'));
            }
          }, pollIntervalMs);
        });

        try {
          const newAccessToken = await waitForTokenRefresh;
          isRefreshing = false;
          if (newAccessToken) {
            onRefreshSuccess(newAccessToken);
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshFailure(
            refreshError instanceof Error ? refreshError : new Error('Token refresh failed'),
          );
          console.warn('[Auth] Token refresh failed in interceptor:', refreshError);
        }
      }

      return Promise.reject(error);
    },
  );
};

export default setupAxios;
